import React from 'react';
import { IconColors } from './PanelIcon';
import { findInputFieldByGdxItemId } from '../profiles/productsUtil';

/**
 * Defines the available panels and bundles for CSS ALCAT testing, along with utility functions to 
 * get the active bundles and panels based on an orderableItem's inputFields.
 */

/**
 * @typedef {object} Panel
 * @readonly
 * @property {int} gdxItemId
 * @property {IconColors} iconColor
 * @property {string} description
 * @property {'food' | 'advanced'} type
 * @property {int} itemCount
 */
export const AlcatPanels = Object.freeze({
	'250 Foods': {
		gdxItemId: 6927,
		iconColor: IconColors.LightGreen,
		description: 'Food Panel',
		type: 'food',
		itemCount: 250
	},
	'200 Veg': {
		gdxItemId: 6931,
		iconColor: IconColors.LightGreen,
		description: 'Vegetarian Panel',
		type: 'food',
		itemCount: 200
	},
	'200 Foods': {
		gdxItemId: 6928,
		iconColor: IconColors.LightGreen,
		description: 'Food Panel',
		type: 'food',
		itemCount: 200
	},
	'150 Veg': {
		gdxItemId: 6932,
		iconColor: IconColors.LightGreen,
		description: 'Vegetarian Panel',
		type: 'food',
		itemCount: 150
	},
	'150 Foods': {
		gdxItemId: 6929,
		iconColor: IconColors.LightGreen,
		description: 'Food Panel',
		type: 'food',
		itemCount: 150
	},
	'100 Foods': {
		gdxItemId: 6930,
		iconColor: IconColors.LightGreen,
		description: 'Food Panel',
		type: 'food',
		itemCount: 100
	},
	'50 Foods': {
		gdxItemId: 6953,
		iconColor: IconColors.LightGreen,
		description: 'Food Panel',
		type: 'food',
		itemCount: 50
	},
	'20 Antibiotics': {
		gdxItemId: 6933,
		iconColor: IconColors.Orange,
		description: 'Antibiotics & Anti-Inflammatory Agents',
		type: 'advanced',
		itemCount: 20
	},
	'30 Food Additives': {
		gdxItemId: 6934,
		iconColor: IconColors.Orange,
		description: 'Food Additives, Colorings, and Preservatives',
		type: 'advanced',
		itemCount: 30
	},
	'40 Preservatives': {
		gdxItemId: 6935,
		iconColor: IconColors.Orange,
		description: 'Preservatives, Additives, & Environmental Chemicals',
		type: 'advanced',
		itemCount: 40
	},
	'50 Herbs': {
		gdxItemId: 6936,
		iconColor: IconColors.Green,
		description: 'Functional Foods & Medicinal Herbs',
		type: 'advanced',
		itemCount: 50
	},
	'50 Female Herbs': {
		gdxItemId: 6937,
		iconColor: IconColors.Green,
		description: 'Female Herbs',
		type: 'advanced',
		itemCount: 50
	},
	'40 Male Herbs': {
		gdxItemId: 6938,
		iconColor: IconColors.Green,
		description: 'Male Herbs',
		type: 'advanced',
		itemCount: 40
	},
	'20 Molds': {
		gdxItemId: 6939,
		iconColor: IconColors.Yellow,
		description: 'Molds',
		type: 'advanced',
		itemCount: 21
	}
});

export const Bundles = {
	'MDVIPWorks': {
		gdxItemId: 6980,
		title: 'MDVIP - The Works',
		customProfile: true,
		panels: [
			{ ...AlcatPanels['250 Foods'], description: 'Foods' },
			AlcatPanels['50 Herbs'],
			{ ...AlcatPanels['50 Female Herbs'], description: 'Female Herbs or 40 Male Herbs' },
			{ ...AlcatPanels['40 Male Herbs'], element: <></> }, // Include for "Advanced Panels" selection, but don't display in bundle card
			AlcatPanels['30 Food Additives'],
			{ description: 'Customized Meal Planning Tools e-booklet' }
		]
	},
	'MDVIPBasic': {
		gdxItemId: 7007,
		title: 'MDVIP - Basic',
		customProfile: true,
		panels: [
			{ ...AlcatPanels['200 Foods'], description: 'Foods' },
			AlcatPanels['30 Food Additives'],
			{ description: 'Customized Meal Planning Tools e-booklet' }
		]
	},
	'EUComprehensiveProfile': {
		gdxItemId: 7004,
		title: 'EU Comprehensive Profile',
		customProfile: true,
		panels: []
	},
	'HamptonPanel': {
		gdxItemId: 7005,
		title: 'Hampton Panel',
		customProfile: true,
		panels: []
	},
	'HamptonComprehensivePanel': {
		gdxItemId: 7006,
		title: 'Hampton Comprehensive Panel',
		customProfile: true,
		panels: []
	},
	'Platinum Plus': {
		gdxItemId: 6921,
		title: 'PLATINUM PLUS',
		itemCount: 370,
		panels: [
			{ ...AlcatPanels['250 Foods'], description: 'Foods' },
			AlcatPanels['50 Herbs'],
			AlcatPanels['30 Food Additives'],
			AlcatPanels['20 Antibiotics'],
			{ ...AlcatPanels['20 Molds'], itemCount: 20 }
		]
	},
	'Platinum Comprehensive': {
		gdxItemId: 6922,
		title: 'PLATINUM Comprehensive',
		itemCount: 320,
		panels: [
			{ ...AlcatPanels['200 Foods'], description: 'Foods' },
			AlcatPanels['50 Herbs'],
			AlcatPanels['30 Food Additives'],
			AlcatPanels['20 Antibiotics'],
			{ ...AlcatPanels['20 Molds'], itemCount: 20 }
		]
	},
	'Comprehensive Wellness 1': {
		gdxItemId: 6923,
		title: 'Comprehensive WELLNESS 1',
		itemCount: 250,
		panels: [
			{ ...AlcatPanels['200 Foods'], description: 'Foods' },
			AlcatPanels['30 Food Additives'],
			{ ...AlcatPanels['20 Molds'], itemCount: 20 }
		]
	},
	'Comprehensive Wellness 2': {
		gdxItemId: 6924,
		title: 'Comprehensive WELLNESS 2',
		itemCount: 230,
		panels: [{ ...AlcatPanels['200 Foods'], description: 'Foods' }, AlcatPanels['30 Food Additives']]
	},
	'Comprehensive Wellness 3': {
		gdxItemId: 6925,
		title: 'Comprehensive WELLNESS 3',
		itemCount: 200,
		panels: [
			{ ...AlcatPanels['150 Foods'], description: 'Foods' },
			AlcatPanels['30 Food Additives'],
			{ ...AlcatPanels['20 Molds'], itemCount: 20 }
		]
	},
	'Pediatric Wellness': {
		gdxItemId: 6926,
		title: 'PEDIATRIC WELLNESS',
		itemCount: 81,
		panels: [
			{ ...AlcatPanels['50 Foods'], description: 'Foods' },
			AlcatPanels['30 Food Additives'],
			{
				iconColor: IconColors.Yellow,
				itemCount: 1,
				description: <i>Candida albicans</i>
			}
		]
	}
};

/**
 * Loop through the defined Bundles and return the ones that are in the orderableItem's inputFields.
 * The returned bundle object will be updated to include the inputField id and name.
 */
export const getActiveBundles = ({ orderableItem }) => {
	if (!orderableItem?.inputFields?.length) {
		return [];
	}

	return Object.entries(Bundles)
		.map(([bundleName, data]) => {
			const inputField = findInputFieldByGdxItemId({ orderableItem, gdxItemId: data?.gdxItemId });

			if (!inputField) {
				return null;
			}

			data.panels
				.filter((panel) => panel.gdxItemId)
				.forEach((panel) => {
					const panelInputField = getActivePanels({ orderableItem, gdxItemId: panel.gdxItemId });
					panel.id = panelInputField?.[0]?.id;
				});

			return { ...data, title: inputField.name, id: inputField.id };
		})
		.filter(Boolean);
};

export const getActivePanels = ({ orderableItem, type, name, gdxItemId }) => {
	if (!orderableItem?.inputFields?.length) {
		return [];
	}

	return Object.entries(AlcatPanels)
		.filter(([panelName, panelData]) => !name || panelName === name)
		.filter(([panelName, panelData]) => !type || panelData.type === type)
		.filter(([panelName, panelData]) => !gdxItemId || panelData.gdxItemId === gdxItemId)
		.map(([panelName, panelData]) => {
			const inputField = findInputFieldByGdxItemId({ orderableItem, gdxItemId: panelData?.gdxItemId });
			if (!inputField) {
				return null;
			}
			return { ...panelData, title: inputField.name, id: inputField.id };
		})
		.filter(Boolean);
};
